import type AnalysisSubject from './AnalysisSubject';
import Dates from '../dates';
import type { StudioRequestSubject, SubjectWithOptionalFee, WorkspaceFeeLimits } from 'venn-state';
import {
  findInPortfolio,
  getPortfolioCashNodes,
  getStudioRequestSubjectCashNodes,
  recursiveGetFunds,
} from '../portfolio';
import { compact } from 'lodash';
import type { Portfolio } from 'venn-api';

export const getSecondaryDisplayLabel = (
  subject: AnalysisSubject,
  savedVersionPrefix?: string,
  nonSavedVersionSuffix?: string,
): string => {
  if (subject.secondaryLabel === 'Last Saved') {
    return subject.secondaryPortfolio?.updated !== undefined
      ? `${savedVersionPrefix ? `${savedVersionPrefix} ` : ''}${Dates.toDayMonthShortYearTime(
          subject.secondaryPortfolio.updated,
        )}`
      : '';
  }
  return `${subject.secondaryLabel}${nonSavedVersionSuffix ? ` ${nonSavedVersionSuffix}` : ''}`;
};

export const getSubjectFunds = (
  subject: AnalysisSubject | StudioRequestSubject | undefined,
  filterZeroAllocations: boolean,
) => (subject?.fund ? [subject.fund] : recursiveGetFunds(subject?.portfolio, filterZeroAllocations));

export const isRequestSubject = (
  subject: StudioRequestSubject | SubjectWithOptionalFee,
): subject is StudioRequestSubject => 'id' in subject;

export const getBaseFee = (subject: StudioRequestSubject | SubjectWithOptionalFee) => {
  const id = isRequestSubject(subject) ? subject.id : (subject.portfolioId ?? subject.fundId);
  return (id && subject.feesMapping?.[id]) || 0;
};

/**
 * Returns the workspace default advisory fee as a decimal fraction (e.g. 1.5% → 0.015),
 * or undefined when no fee is configured.
 */
export const getWorkspaceDefaultFee = (workspaceFeeLimits: WorkspaceFeeLimits | undefined): number | undefined => {
  if (workspaceFeeLimits?.defaultFee === undefined) {
    return undefined;
  }
  return (workspaceFeeLimits.defaultFee ?? 0) / 100.0;
};

export const hasFeesExcluded = (subject: StudioRequestSubject) =>
  Object.keys(subject.feesMapping ?? {}).length > 1 ||
  getPortfolioCashNodes(subject.modifiedPortfolio ?? subject.portfolio).length > 0;

export const hasAllFeesExcluded = (subject: StudioRequestSubject) => {
  return getSubjectFunds(subject, false).length === getSubjectNodesWithExcludedFees([subject])[0]!.length;
};

export const hasFeesAndExcludedInvestments = ({
  subject,
  displayZeroFee = false,
}: {
  subject: StudioRequestSubject;
  displayZeroFee?: boolean;
}): boolean => (displayZeroFee || !!getBaseFee(subject)) && hasFeesExcluded(subject);

export const getSubjectNodesWithExcludedFees = (subjectsWithExcludedFees: StudioRequestSubject[]): Portfolio[][] =>
  subjectsWithExcludedFees.map((subject) => [
    ...compact(
      subject.feesMapping
        ? Object.keys(subject.feesMapping)
            .filter((key) => subject.feesMapping?.[key] === 0 && key !== subject.id)
            .map((node) => findInPortfolio(subject.portfolio!, Number(node)))
        : [],
    ),
    ...getStudioRequestSubjectCashNodes(subject),
  ]);

export const updateFilteredSubjectFields = (subject: StudioRequestSubject) =>
  subject.strategy?.fund && subject.strategy.fund.id
    ? ({
        ...subject,
        subjectType: 'INVESTMENT',
      } as StudioRequestSubject)
    : subject;

const collectIdsInTree = (node: Portfolio) => {
  const ids = [node.id];
  node.children?.forEach((child) => ids.push(...collectIdsInTree(child)));
  return ids;
};

export const adjustFeesMappingForDescendantNode = (
  descendant: StudioRequestSubject,
  baseMapping: Record<string, number>,
  parentId: number,
) => {
  if (descendant.portfolio) {
    if (descendant.portfolio.historical) {
      return {};
    }

    const descendantStrategyId = descendant.strategy?.id ?? descendant.portfolio.id;
    const allDescendantIds = collectIdsInTree(descendant.strategy ?? descendant.portfolio);
    const newMapping: Record<string, number> = {};
    let numberOfExclusions = 0;
    allDescendantIds.forEach((id) => {
      if (baseMapping[id] !== undefined) {
        newMapping[id] = baseMapping[id];
        if (baseMapping[id] === 0) {
          numberOfExclusions++;
        }
      }
    });

    if (allDescendantIds.length > 1 && numberOfExclusions === allDescendantIds.length - 1) {
      // If all descendants have excluded fees, then don't show the fee on this node either.
      newMapping[descendant.id] = 0;
    } else if (baseMapping[descendantStrategyId] === undefined && baseMapping[parentId] !== undefined) {
      newMapping[descendant.id] = baseMapping[parentId];
    }

    return newMapping;
  }

  return baseMapping;
};

export const getRequestSubjectDisplayZeroFee = ({
  displayZeroFeeConfigValue,
  subject,
  isReport,
  displayDefaultFee,
  displayInvestmentFee,
  isBenchmark,
}: {
  displayZeroFeeConfigValue: boolean;
  subject: StudioRequestSubject;
  isReport: boolean;
  displayDefaultFee: boolean;
  displayInvestmentFee: boolean;
  isBenchmark?: boolean;
}): boolean => {
  const isHistorical = isHistoricalSubject(subject);
  const isInvestment = subject.subjectType === 'INVESTMENT';
  const isPrivate = subject.private;
  return (
    !isHistorical &&
    !isPrivate &&
    displayZeroFeeConfigValue &&
    displayDefaultFee &&
    isReport &&
    (!isInvestment || displayInvestmentFee) &&
    subject?.fund?.assetType !== 'BENCHMARK' &&
    !isBenchmark
  );
};

export const getRequestSubjectFee = ({
  subject,
  displayZeroFee,
  displayInvestmentFee,
  isBenchmark,
  isReport,
}: {
  subject: StudioRequestSubject;
  displayZeroFee: boolean;
  displayInvestmentFee: boolean;
  isBenchmark: boolean | undefined;
  isReport?: boolean | undefined;
}): number | undefined => {
  if (
    (subject.subjectType === 'INVESTMENT' && !displayInvestmentFee) ||
    isBenchmark ||
    subject?.fund?.assetType === 'BENCHMARK' ||
    isHistoricalSubject(subject) ||
    !isReport
  ) {
    return undefined;
  }
  const subjectHasAllFeesExcluded = hasAllFeesExcluded(subject);

  if (subjectHasAllFeesExcluded) {
    return displayZeroFee ? 0 : undefined;
  }
  const fee = getBaseFee(subject);
  if (!fee && displayZeroFee) {
    return 0;
  }
  return fee || undefined;
};

const isHistoricalSubject = (subject?: StudioRequestSubject) =>
  (subject?.modifiedPortfolio ?? subject?.portfolio)?.historical ?? false;
